import format from 'date-fns/format';
import * as React from 'react';
import { CartesianGrid, Label, Line, LineChart, TooltipProps, XAxis, YAxis } from 'recharts';

import InfoToolTip from 'DesignSystem/features/InfoTooltip';
import { ChartConfig, ChartContainer, ChartTooltip } from 'DesignSystem/ui/chart';
import type { ItemTimeSeriesData } from 'pages/preference-cards/types';

const TOOLTIP_TEXT = 'Average number of units used per case for the selected item.';

const chartConfig = {
  average: {
    label: 'Avg. Units',
    color: 'hsl(var(--chart-primary))',
  },
} satisfies ChartConfig;

interface ChartDataPoint {
  average: number;
  month: string;
  start_date: string;
  end_date: string;
}

type TimeSeriesChartProps = {
  timeSeries: ItemTimeSeriesData[];
};

export function TimeSeriesChart({ timeSeries }: TimeSeriesChartProps) {
  const data: ChartDataPoint[] = React.useMemo(() => {
    return timeSeries.map((t) => ({
      average: Math.round(t.average),
      month: format(new Date(t.start_date), 'MMM'),
      start_date: t.start_date,
      end_date: t.end_date,
    }));
  }, [timeSeries]);

  return (
    <div className="flex flex-col gap-0.5">
      <div className="type-body-xs flex items-center font-medium text-zinc-800">
        <InfoToolTip info={TOOLTIP_TEXT} iconClassName="h-4 w-4">
          Used Per Case
        </InfoToolTip>
      </div>
      <div className="type-body-2xs leading-4 text-gray-400">Last 12 months</div>
      <ChartContainer config={chartConfig} className="mt-1 h-full min-h-[200px] w-full">
        <LineChart data={data}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="month"
            tickLine={false}
            tickMargin={3}
            axisLine={false}
            tickFormatter={(value) => value.slice(0, 3)}
            fontSize={9}
            tickCount={12}
            height={40}
          >
            <Label value="Month" offset={0} position="insideBottom" />
          </XAxis>

          <YAxis
            dataKey="average"
            allowDecimals={false}
            type="number"
            tickLine={false}
            tickMargin={0}
            axisLine={false}
            fontSize={9}
            width={25}
          >
            <Label
              value="Avg Units Per Case"
              offset={0}
              angle={-90}
              position="insideLeft"
              style={{ textAnchor: 'middle' }}
            />
          </YAxis>

          <Line dataKey="average" stroke="var(--color-average)" strokeWidth={2} dot={false} type="linear" />
          <ChartTooltip cursor={false} content={CustomTooltip} />
        </LineChart>
      </ChartContainer>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    const payloadItem = payload[0];
    const data = payloadItem.payload as ChartDataPoint;
    const value = payloadItem.value;

    if (typeof value !== 'number' || !data?.start_date) {
      return null;
    }

    const isZero = value === 0;

    return (
      <div className="type-body-2xs grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-background px-2.5 py-1.5 text-zinc-800 shadow-xl">
        <div className="flex flex-col gap-2">
          <div className="type-body-2xs mb-1.5 font-medium">
            {label} {format(new Date(data.start_date), 'yyyy')}
          </div>
          <div className="type-body-2xs">
            {isZero ? (
              <div className="flex flex-col gap-2 text-amber-600">
                <span>No units used</span>
                <div className="flex flex-col gap-1 text-xs text-gray-500">
                  <span>Note: This could mean either:</span>
                  <span>• No cases used this item</span>
                  <span>• No cases occurred this month</span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between gap-1">
                <span>Avg. Units</span>
                <span>{value}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};
